import { useQuery } from "@tanstack/react-query";

import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { useMemo } from "react";

import type { InferTransformedProcedureOutput } from "@goose/api";
import type { OpportunityId, OrganizationId } from "@goose/schema/Salesforce";

import { DataTable } from "~/components/DataTable";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Spinner } from "~/components/ui/Spinner";
import { formatDate } from "~/lib/format";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/opportunities/$opportunityId/contacts",
)({
  component: OpportunityContacts,
});

function OpportunityContacts() {
  const trpc = useTRPC();
  const { organizationId, opportunityId } = Route.useParams();

  const contacts = useQuery(
    trpc.contacts.listByOpportunity.queryOptions({
      organizationId,
      opportunityId,
    }),
  );

  return (
    <QueryState
      result={contacts}
      loading={
        <div className="flex justify-center items-center py-24">
          <Spinner size="lg" />
        </div>
      }
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(data) => (
        <>
          <ContactsTable
            organizationId={organizationId}
            opportunityId={opportunityId}
            data={data}
          />
          <Outlet />
        </>
      )}
    </QueryState>
  );
}

type ContactsList =
  InferTransformedProcedureOutput<"contacts.listByOpportunity">;

interface ContactsTableProps {
  organizationId: OrganizationId;
  opportunityId: OpportunityId;
  data: ContactsList;
}

function ContactsTable({
  organizationId,
  opportunityId,
  data,
}: ContactsTableProps) {
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<ContactsList[number]>();
    return [
      columnHelper.accessor("id", {
        header: "Contact",
        cell: (ctx) => (
          <Link
            className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
            to="/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId"
            params={{
              organizationId,
              opportunityId,
              contactId: ctx.getValue(),
            }}
          >
            {ctx.getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor("latestEvaluationScore", {
        header: "Latest Score",
        cell: (ctx) => {
          const latestEvaluationScore = ctx.getValue();
          return `${Math.round(latestEvaluationScore * 100)}%`;
        },
      }),
      columnHelper.accessor("latestActivityAt", {
        header: "Last Activity",
        cell: (ctx) => formatDate(ctx.getValue()),
      }),
    ];
  }, [organizationId, opportunityId]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    initialState: {
      sorting: [{ id: "latestActivityAt", desc: true }],
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <DataTable
      title="Contacts"
      emptyMessage="No contacts found"
      table={table}
      onRowClick={(row) => {
        void navigate({
          to: "/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId",
          params: {
            organizationId,
            opportunityId,
            contactId: row.original.id,
          },
        });
      }}
    />
  );
}
